<template>
  <modal-view
    @close-modal="onClose"
    title="Transfer Store Ownership"
    customMaxWidth="520px"
  >
    <div class="transfer-ownership-modal">
      <div class="transfer-ownership-modal__store-info">
        <p class="transfer-ownership-modal__label">Store</p>
        <p class="transfer-ownership-modal__value">
          {{ store.name }}
          <span class="transfer-ownership-modal__meta">ID {{ store.id }}</span>
        </p>
        <p class="transfer-ownership-modal__label transfer-ownership-modal__label--mt">
          Current owner ID
        </p>
        <p class="transfer-ownership-modal__value">{{ store.owner_id }}</p>
      </div>

      <div class="transfer-ownership-modal__lookup">
        <div class="transfer-ownership-modal__lookup-row">
          <hs-input
            v-model="phoneNumber"
            label="New owner phone number"
            placeholder="+250..."
            type="tel"
            :disabled="transferring"
            @keyup.enter="handleLookup"
          />
          <hs-button
            variant="secondary"
            icon="search"
            :loading="loadingLookup"
            :disabled="!phoneNumber.trim() || transferring"
            @click="handleLookup"
          >
            Look up
          </hs-button>
        </div>

        <hs-alert
          v-if="lookupError"
          tone="danger"
          :message="lookupError"
        />

        <div v-if="resolvedUser" class="transfer-ownership-modal__user-card">
          <hs-icon name="user" :size="16" />
          <div>
            <p class="transfer-ownership-modal__user-name">
              {{ resolvedUserDisplayName }}
            </p>
            <p class="transfer-ownership-modal__user-meta">
              ID {{ resolvedUser.id }} · {{ resolvedUser.phone_number }}
            </p>
          </div>
        </div>

        <hs-alert
          v-if="isCurrentOwner"
          tone="warning"
          message="This user is already the current store owner."
        />
      </div>

      <hs-alert
        tone="warning"
        title="This action is irreversible from the UI"
        message="The current owner will lose ownership. Their existing store roles are not removed."
      />

      <hs-alert
        v-if="serverError"
        tone="danger"
        :message="serverError"
      />

      <div class="transfer-ownership-modal__actions">
        <hs-button variant="secondary" :disabled="transferring" @click="onClose">
          Cancel
        </hs-button>
        <hs-button
          variant="danger"
          icon="refresh-cw"
          :loading="transferring"
          :disabled="!canTransfer"
          @click="handleConfirm"
        >
          Transfer ownership
        </hs-button>
      </div>
    </div>
  </modal-view>
</template>

<script>
import { ref, computed } from "vue";
import { useStore } from "vuex";
import ModalView from "@/components/modals/ModalView.vue";
import { HsAlert, HsButton, HsInput } from "@/components/ui";
import { HsIcon } from "@/components/ui";

export default {
  name: "TransferOwnershipModal",
  components: { ModalView, HsAlert, HsButton, HsInput, HsIcon },
  props: {
    store: {
      type: Object,
      required: true,
    },
  },
  emits: ["close", "transferred"],
  setup(props, { emit }) {
    const vuexStore = useStore();

    const phoneNumber = ref("");
    const lookupError = ref("");
    const serverError = ref("");

    const resolvedUser = computed(
      () => vuexStore.getters["adminStoreManagement/lookupUser"]
    );
    const loadingLookup = computed(
      () => vuexStore.getters["adminStoreManagement/loadingLookup"]
    );
    const transferring = computed(
      () => vuexStore.getters["adminStoreManagement/transferring"]
    );
    const resolvedUserDisplayName = computed(
      () =>
        resolvedUser.value?.names ||
        resolvedUser.value?.phone_number ||
        `User #${resolvedUser.value?.id || ""}`
    );
    const canTransfer = computed(
      () =>
        Boolean(resolvedUser.value?.id) &&
        Number(resolvedUser.value.id) !== Number(props.store.owner_id) &&
        !transferring.value
    );
    const isCurrentOwner = computed(
      () =>
        Boolean(resolvedUser.value?.id) &&
        Number(resolvedUser.value.id) === Number(props.store.owner_id)
    );

    const handleLookup = async () => {
      const phone = phoneNumber.value.trim();
      if (!phone) return;
      lookupError.value = "";
      try {
        const user = await vuexStore.dispatch(
          "adminStoreManagement/lookupUserByPhone",
          phone
        );
        if (!user) {
          lookupError.value = "No user found with that phone number.";
        }
      } catch (err) {
        lookupError.value =
          err?.response?.data?.error?.message ||
          "Failed to look up user. Check the phone number and try again.";
      }
    };

    const handleConfirm = async () => {
      if (!canTransfer.value) {
        if (Number(resolvedUser.value?.id) === Number(props.store.owner_id)) {
          serverError.value = "New owner is already the current store owner.";
        }
        return;
      }
      serverError.value = "";
      try {
        const updatedStore = await vuexStore.dispatch(
          "adminStoreManagement/transferOwnership",
          {
            storeId: props.store.id,
            newOwnerId: resolvedUser.value.id,
          }
        );
        vuexStore.commit("adminStoreManagement/RESET_LOOKUP");
        emit("transferred", updatedStore);
      } catch (err) {
        serverError.value =
          err?.response?.data?.error?.message ||
          "Transfer failed. Please try again.";
      }
    };

    const onClose = () => {
      vuexStore.commit("adminStoreManagement/RESET_LOOKUP");
      emit("close");
    };

    return {
      phoneNumber,
      lookupError,
      serverError,
      resolvedUser,
      resolvedUserDisplayName,
      loadingLookup,
      transferring,
      canTransfer,
      isCurrentOwner,
      handleLookup,
      handleConfirm,
      onClose,
    };
  },
};
</script>

<style scoped>
.transfer-ownership-modal {
  display: grid;
  gap: 1.25rem;
  padding: 0.25rem 0 0.5rem;
}

.transfer-ownership-modal__store-info {
  background: color-mix(in srgb, var(--hs-bg-soft, #f8fafc) 70%, var(--hs-surface, #fff));
  border: 1px solid var(--hs-border, rgba(148, 163, 184, 0.22));
  border-radius: 10px;
  display: grid;
  gap: 0.15rem;
  padding: 0.9rem 1rem;
}

.transfer-ownership-modal__label {
  color: var(--hs-muted, #64748b);
  font-size: 0.72rem;
  font-weight: 800;
  margin: 0;
  text-transform: uppercase;
}

.transfer-ownership-modal__label--mt {
  margin-top: 0.6rem;
}

.transfer-ownership-modal__value {
  color: var(--hs-text, #0f172a);
  font-size: 0.9rem;
  font-weight: 900;
  margin: 0;
}

.transfer-ownership-modal__meta {
  color: var(--hs-muted, #64748b);
  font-size: 0.76rem;
  font-weight: 750;
  margin-left: 0.4rem;
}

.transfer-ownership-modal__lookup {
  display: grid;
  gap: 0.75rem;
}

.transfer-ownership-modal__lookup-row {
  align-items: end;
  display: grid;
  gap: 0.65rem;
  grid-template-columns: minmax(0, 1fr) auto;
}

.transfer-ownership-modal__user-card {
  align-items: center;
  background: color-mix(in srgb, var(--hs-success, #16a34a) 8%, var(--hs-surface, #fff));
  border: 1px solid color-mix(in srgb, var(--hs-success, #16a34a) 30%, transparent);
  border-radius: 8px;
  color: var(--hs-text, #0f172a);
  display: flex;
  gap: 0.65rem;
  padding: 0.75rem 1rem;
}

.transfer-ownership-modal__user-name {
  font-size: 0.88rem;
  font-weight: 900;
  margin: 0;
}

.transfer-ownership-modal__user-meta {
  color: var(--hs-muted, #64748b);
  font-size: 0.76rem;
  font-weight: 750;
  margin: 0.1rem 0 0;
}

.transfer-ownership-modal__actions {
  display: flex;
  gap: 0.65rem;
  justify-content: flex-end;
}
</style>
